import axios, { AxiosResponse, AxiosError } from "axios";
import { CurrentUserResponse } from "@type/auth";
import { getCookie } from "@util/cookie";
import { CompanyPermissionParams, InviteCompanyPermissionRequest, RemoveCompanyPermissionRequest, UpdateApplicantInfoRequest, UpdateCompanyBillingInfoRequest, UpdateCompanyMetadataRequest, UpdateNotificationPreferencesRequest, WorkspaceUpdateCompanyInfo } from "@type/company";
import {
  SearchShortlistedCandidateRequest,
  ShortlistedCounts,
  ShortlistedCountsRequest,
} from "@type/candidate";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

/**
 * Gets the current authenticated user's information
 * @returns Promise with current user data
 */
export const getCurrentUser = async (): Promise<
  CurrentUserResponse | undefined
> => {
  try {
    const token = getCookie("authToken");
    if (!token) {
      return undefined;
    }
    const response: AxiosResponse = await axios({
      url: `${apiURL}/auth/me`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      timeout: 10000, // 10 second timeout
    });

    return {
      success: true,
      status: response.status,
      data: response.data,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED"
          ? "Request timed out"
          : "Failed to get user data"),
      error: err,
    };
  }
};

export const getStartupProfile = async (userSubmissionID: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    method: "GET",
    url: `${apiURL}/startup/${userSubmissionID}/profile`,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

export const getCompanyInfo = async (companyID: string): Promise<any> => {
  if (!companyID) {
    return Promise.reject(new Error("company_id is required"));
  }
  return await axios({
    url: `${apiURL}/companies/info/${companyID}`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const updateCompanyNotificationPreferences = async (payload: UpdateNotificationPreferencesRequest): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/companies/notification-email-preferences`,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: payload
  });
};

export const getCompanyPermissions = async (params: CompanyPermissionParams): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/companies/permissions`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    params
  });
}

export const getSearchCompanyPermissions = async (keywords: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/companies/permission/search`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    params: {
      keywords,
    }
  });
}

export const inviteCompanyPermissions = async (payload: InviteCompanyPermissionRequest): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/companies/invite`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: payload
  });
}

export const removeCompanyPermissions = async (payload: RemoveCompanyPermissionRequest): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/companies/member`,
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: payload
  });
};

export const updateCompanyPermissions = async (userId: string, permission: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/companies/permissions`,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: {
      user_id: userId,
      Permission: permission
    }
  });
};

export const updatePreferenceCompanyLogo = async (file: File) => {
  const token = getCookie("authToken") || getCookie("tempAuthToken");

  const formData = new FormData();
  formData.append("file", file);

  const resp = await axios({
    url: `${apiURL}/companies/update-preference-logo`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: formData,
  });

  return resp;
}

export const updatePreferenceCompanyAILogo = async (file: File) => {
  const token = getCookie("authToken");

  const formData = new FormData();
  formData.append("file", file);

  const resp = await axios({
    url: `${apiURL}/companies/update-preference-ai-logo`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: formData,
  });

  return resp;
}
export const updatePreferenceColor = async (color: string) => {
  const token = getCookie("authToken");

  const resp = await axios({
    url: `${apiURL}/companies/update-preference-color`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: {
      primary_color: color,
    },
  });

  return resp;
}

export const getCompanyInfoBySubdomain = async (subdomain: string): Promise<any> => {
  return await axios({
    url: `${apiURL}/companies/subdomain/${subdomain}`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const getBookmaredCandidates = async (): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/candidates/bookmarked`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export const getShortlistedCandidates = async (): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/candidates/shortlisted`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export const bookmarkingCandidate = async (candidate_id: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/candidates/bookmark`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: {
      candidate_id
    }
  });
};

export const removeBookmarkCandidate = async (candidate_id: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/candidates/remove-bookmark`,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: {
      candidate_id
    }
  });
}

export const getShortlistedByCandidateID = async (candidate_id: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/candidates/shortlisted/${candidate_id}`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export const searchShortlistedCandidate = async (params: SearchShortlistedCandidateRequest): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/search/shortlisted`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    params
  });
}

// Returns `{ all, favorites, comments }` counts so the Review tab chip badges
// stay accurate regardless of which `type` filter is currently active.
export const getShortlistedCounts = async (
  params: ShortlistedCountsRequest,
): Promise<AxiosResponse<{ code: number; message: string; data: ShortlistedCounts }>> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/search/shortlisted/counts`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    params,
  });
}

export const getShortlistedByCandidateAndJobID = async (candidate_id: string, job_id: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/candidates/shortlisted/${candidate_id}/${job_id}`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export const updateCompanyMetadata = async (payload: UpdateCompanyMetadataRequest): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/companies/update-metadata`,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: payload
  });
};

export const updateApplicantInfo = async (applicant_id: string, payload: UpdateApplicantInfoRequest): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/candidates/${applicant_id}/update-info`,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: payload
  });
};

export const updateCompanyInfo = async (payload: WorkspaceUpdateCompanyInfo): Promise<any> => {
  const token = getCookie("authToken") || getCookie("tempAuthToken");
  return await axios({
    url: `${apiURL}/companies/update-info`,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: payload
  });
};

export const getCompanyBillingInfo = async (): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/companies/billing-info`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

export const updateCompanyBillingInfo = async (body: UpdateCompanyBillingInfoRequest): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/companies/billing-info`,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: body
  });
};